import React, { useEffect, useMemo, useRef, useState } from "react";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { LngLatLike, Map, MapboxGeoJSONFeature, FlyToOptions } from "mapbox-gl";
import StandardPopup from "./types/popup.standard";
import { center } from "@turf/turf";
import { LocationData, MapTypeData } from "../map.interfaces";
import LinerServicesPopup from "./types/popup.linerServices";
import { CmsMapData } from "../hooks/useCmsMapData";
import { MapTypes } from "../mapConfig";
import { Route } from "../../linerServiceAdminRoute";

export interface IMapPopupPosition {
  top?: number;
  left?: number;
}

export interface IMapPopupProps {
  map: Map | null;
  selectedLocationData: LocationData | undefined;
  mapConfig: MapTypeData | undefined;
  position: IMapPopupPosition;
  currentCmsMapData: CmsMapData;
  onClose?: Function;
  onOpen?: Function;
}

const MapPopup = ({
  map,
  selectedLocationData,
  mapConfig,
  position,
  currentCmsMapData,
  onClose,
  onOpen,
}: IMapPopupProps) => {
  const isMobile = useMediaQuery(576);
  const ref = useRef<HTMLDivElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [widePopup, setWidePopup] = useState(false);
  const [previousMapPosition, setPreviousMapPosition] = useState<FlyToOptions | null>(null);

  useEffect(() => {
    if (map && selectedLocationData) {
      setShowPopup(false);

      const pitch =
        selectedLocationData.propertiesForLayer?.pitch || map.getPitch();
      const zoom =
        currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP
          ? 3.5
          : selectedLocationData.propertiesForLayer?.zoom || map.getZoom();
      const bearing =
        selectedLocationData.propertiesForLayer?.bearing || map.getBearing();

      let zoomPosition: LngLatLike = [
        selectedLocationData.lngLat.lng,
        selectedLocationData.lngLat.lat,
      ];

      const geometryType = selectedLocationData.features[0].geometry.type;

      if (geometryType !== "GeometryCollection") {
        const centerPoint = center(selectedLocationData.features[0].geometry);

        zoomPosition = [
          centerPoint.geometry.coordinates[0],
          centerPoint.geometry.coordinates[1],
        ];
      }

      let validPopup = false;

      if (currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP) {
        setWidePopup(true);
        //Liner service map validation, must have a name

        let hasPlaceName = false;
        for (const key in selectedLocationData.propertyData) {
          if (key.includes("name")) {
            hasPlaceName = true;
          }
        }

        validPopup = hasPlaceName;
        if (selectedLocationData?.propertyData?.routes) {
          const routes: Route[] = JSON.parse(
            selectedLocationData.propertyData.routes
          ) as Route[];
          if (!routes?.length) {
            validPopup = false;
          }
        }
      } else {
        //Standard map validation. must have body
        validPopup = !!selectedLocationData?.propertiesForLayer?.body;
      }

      const popupWidth = ref.current?.offsetWidth || 0;

      let rightPadding =
        !validPopup || isMobile || popupWidth === 0 ? 0 : popupWidth + 100;

      // Store the map position to be able to return to it on popup close
      setPreviousMapPosition({
        center: map.getCenter(),
        padding: map.getPadding(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        zoom: map.getZoom(),
        essential: true,
      });

      map.flyTo({
        center: zoomPosition,
        padding: { top: 0, bottom: 0, left: 0, right: rightPadding },
        pitch,
        bearing,
        zoom,
        essential: true,
      });

      if (validPopup) {
        setShowPopup(true);
        if (onOpen) {
          onOpen();
        }
      }
    }
  }, [selectedLocationData]);

  // ------------------------------------------------------------
  // useEffect - position
  // ------------------------------------------------------------

  const close = () => {
    setShowPopup(false);
    if (onClose) {
      onClose();
    }
    if (map && mapConfig && !currentCmsMapData.zoomOnClose) {
      if (previousMapPosition !== null){
        map.flyTo(previousMapPosition);
      }
    }
  };

  const Popup = useMemo(() => {
    if (selectedLocationData) {
      if (selectedLocationData.propertiesForLayer) {
        return <StandardPopup locationData={selectedLocationData} />;
      } else {
        if (currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP) {
          return <LinerServicesPopup locationData={selectedLocationData} />;
        }
      }
    }
    return null;
  }, [selectedLocationData]);

  return (
    <div
      ref={ref}
      className={`map-popup ${widePopup ? "wide" : ""} ${
        showPopup ? "show" : "hide"
      }`}
      style={{ top: position.top }}
    >
      <button
        type="button"
        className="map-popup-close-button"
        aria-label="Close popup"
        onClick={close}
      >
        <img alt="map popup close icon" src="/Assets/icons/close_icon.svg" />
      </button>
      <div className="map-popup-content">{Popup}</div>
      <div className={`map-popup-bg ${showPopup ? "show" : "hide"}`}></div>
    </div>
  );
};

export default MapPopup;
